import axios from "axios";
import { handleAxiosError } from "../components/Common/apiHelpers";
import { API_BASE_URL } from "../config";
import store from "../store/store";
import { logout, setCredentials } from "../store/authSlice";

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Nuk dërgohet më `x-otp` nga shfletuesi. Sekreti TOTP duhej të ishte i ngulitur në
// bundle-in publik JS për ta gjeneruar këtu — pra nuk ishte faktor i dytë, ishte thjesht
// publikim i një sekreti të përbashkët me të gjithë klientët (konfirmuar 16.08.2026: fara
// lexohej nga kushdo te main.*.js). Vërtetimi i panelit bëhet me JWT-në më poshtë; serveri
// i përjashton nga OTP-ja thirrësit me bearer token të vlefshëm.
axiosClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("token");
    config.headers = config.headers || {};

    const hasExplicitAuth =
      config.headers.Authorization || config.headers.authorization;
    if (token && !hasExplicitAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // T-3593 (S-3): `REACT_APP_ADMIN_API_KEY` u hoq. Çdo vlerë e build-it të CRA-së përfundon në
    // bundle-in publik JS, pra një çelës `kb_sk_` aty i jepej kujtdo që hap faqen. Paneli
    // vërtetohet vetëm me JWT; serveri i lë adminët pa X-Api-Key (ApiKeyMiddleware.IsSystemAdmin).

    return config;
  },
  (error) => {
    handleAxiosError(error);
    return Promise.reject(error);
  },
);

// The backend rotates (invalidates) the refresh token on every successful
// /auth/refresh call. When several requests fail with 401 at the same time we
// must therefore refresh only once and let the others reuse that result —
// otherwise the second concurrent refresh sends an already-rotated token,
// fails, and logs the user out. This holds the single in-flight refresh.
let refreshPromise = null;

const performTokenRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return null;
  }

  const refreshResponse = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const tokens = refreshResponse?.data?.data;
  if (
    refreshResponse?.status >= 200 &&
    refreshResponse?.status < 300 &&
    refreshResponse?.data?.success &&
    tokens?.accessToken
  ) {
    store.dispatch(setCredentials(tokens));
    return tokens.accessToken;
  }

  return null;
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const statusCode = error?.response?.status;
    const originalRequest = error?.config ?? {};
    const requestUrl = originalRequest?.url ?? "";
    const authHeader =
      originalRequest?.headers?.Authorization ||
      originalRequest?.headers?.authorization ||
      "";
    const isAgentAuthRequest = String(authHeader).includes("kb_");
    const isAuthEndpoint =
      requestUrl.includes("/auth/login") || requestUrl.includes("/auth/refresh");

    // Only 401 means "the token is no longer good". A 403 is a live session that is
    // simply not allowed here (a module a developer lacks, or an account still on its
    // temporary password) — refreshing or logging out would be wrong, so it falls
    // through to the caller as an ordinary error.
    if (statusCode === 401 && !isAuthEndpoint && !isAgentAuthRequest) {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!originalRequest._retry && refreshToken) {
        originalRequest._retry = true;

        try {
          // Reuse the in-flight refresh if one is already running so the
          // rotating refresh token is consumed exactly once.
          if (!refreshPromise) {
            refreshPromise = performTokenRefresh().finally(() => {
              refreshPromise = null;
            });
          }

          const newAccessToken = await refreshPromise;

          if (newAccessToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosClient(originalRequest);
          }
        } catch (refreshError) {
          handleAxiosError(refreshError);
        }
      }

      store.dispatch(logout());

      if (window.location.pathname !== "/unauthorized") {
        window.location.replace("/unauthorized");
      }
    }

    handleAxiosError(error);
    return Promise.reject(error);
  },
);

export default axiosClient;
